import { xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

export const styles = {
  line: (backgroundColor: string, color: string) => ({
    backgroundColor,
    color,
    padding: '10px',
    borderRadius: '5px',
    marginBottom: '10px',
    fontFamily: token('font.family.code'),
  }),
  textarea: {
    backgroundColor: token('color.background.accent.green.subtlest'),
    color: token('color.text.accent.green'),
    padding: '10px',
    borderRadius: '5px',
    border: '1px solid',
    width: '100%',
    height: '50px',
    font: token('font.body.large'),
    marginBottom: '10px',
  },
};

export const SuggestionCard = xcss({
  display: 'flex',
  paddingRight: 'space.150',
  paddingLeft: 'space.150',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
  flexDirection: 'column',
  background: token('elevation.surface'),
  minWidth: 'fit-content',
  borderRadius: '6px',
  border: `1px solid ${token('color.border')}`,
});

export const SuggestionCardHeader = xcss({
  display: 'flex',
  justifyContent: 'center',
  paddingTop: 'space.150',
  paddingBottom: 'space.150',
  alignItems: 'center',
  gap: 'space.150',
  alignSelf: 'stretch',
});

export const SuggestionCardHeaderTitle = xcss({
  font: 'font.body.UNSAFE_small',
  fontWeight: 'font.weight.medium',
  color: 'color.text.subtle',
});

export const SuggestionCardFooter = xcss({
  display: 'flex',
  justifyContent: 'space-between',
  paddingTop: 'space.150',
  paddingBottom: 'space.150',
  alignItems: 'center',
  alignSelf: 'stretch',
});

const chunkAddedCodeBackgroundColor = token('color.background.success');
const chunkRemovedCodeBackgroundColor = token('color.background.danger');

const LINE_COLORS = {
  add: chunkAddedCodeBackgroundColor,
  del: chunkRemovedCodeBackgroundColor,
};

export const SuggestionCardLine = (props: { type: 'del' | 'add' }) =>
  xcss({
    boxSizing: 'border-box',
    background: props.type === 'del' ? LINE_COLORS.del : LINE_COLORS.add,
    display: 'flex',
    alignItems: 'center',
    // Note: `whiteSpace: pre` is on code line to make sure code indentation
    // is not removed by this property
    whiteSpace: 'nowrap',
    width: '100%',
    marginRight: 'space.200',
  });

export const LINE_BEFORE_CONTENT = {
  add: '+',
  del: '-',
};

export const SuggestionCardLineNumbers = (props: {
  gutterWidth: number;
  type: 'del' | 'add';
}) =>
  xcss({
    font: 'font.body.UNSAFE_small',
    fontFamily: 'font.family.code',
    position: 'relative',
    display: 'flex',
    justifyContent: 'flex-end',
    boxSizing: 'border-box',
    background: props.type === 'del' ? LINE_COLORS.del : LINE_COLORS.add,
    color: 'color.text.subtlest',
    width: `${props.gutterWidth}px`,
  });

export const SuggestionCardLineNumber = xcss({
  display: 'inline-flex',
  font: 'font.body.UNSAFE_small',
  fontFamily: 'font.family.code',
  paddingBlock: 'space.025',
  zIndex: '1',
});

export const SuggestionCardCode = (props: { type: 'add' | 'del' }) =>
  xcss({
    font: 'font.body.UNSAFE_small',
    fontFamily: 'font.family.code',
    paddingBlock: 'space.025',
    margin: '0',
    width: '100%',
    boxSizing: 'border-box',
    paddingLeft: 'space.100',
    paddingRight: 'space.100',
    ':before': {
      content: `'${LINE_BEFORE_CONTENT[props.type]}'`,
      paddingRight: 'space.100',
    },
    background: props.type === 'del' ? LINE_COLORS.del : LINE_COLORS.add,
    // Note: This is needed to prevent the whitespace from being cut off of the start
    // of the line (SuggestionCardLine container has `whiteSpace: 'nowrap'` to handle
    // long lines of code)
    whiteSpace: 'pre',
  });
